import {InvoiceSummary, useInvoicesApi} from "../../api";
import {useLocalization} from "../../i18n";
import {hasRoles, useAuthContext, useUser} from "../../context/auth";
import {useFlag} from "@variocube/app-ui";
import React, {Fragment, useEffect} from "react";
import {Button, Dialog, DialogActions} from "@mui/material";
import {inSameInbox, InvoiceRouting} from "./details/routing";
import {useAsync} from "react-async-hook";
import {canRouteInvoice} from "../../utils/canRoute";

interface BulkRouteButtonProps {
	invoices: InvoiceSummary[];
	onRouted: () => any;
}

export function BulkRouteButton({invoices, onRouted}: Readonly<BulkRouteButtonProps>) {
	const {t} = useLocalization();
	const {user} = useAuthContext();

	const [open, setOpen, clearOpen] = useFlag(false);
	const [routed, setRouted, clearRouted] = useFlag(false);

	const visible = hasRoles(user, ["SuperUser", "OebbAdministrator", "OebbUser"]);

	const {loading, result} = useRoutableInvoices(visible, invoices);

	useEffect(() => {
		if (!open && routed) {
			onRouted();
			clearRouted();
		}
	}, [open, routed, onRouted]);

	if (!visible) {
		return null;
	}

	return (
		<Fragment>
			<Button variant="outlined" disabled={loading || result?.length == 0} onClick={setOpen}>
				{t("invoices.details.approvalTitle")}
			</Button>
			<Dialog open={open} onClose={clearOpen} fullWidth maxWidth="xs">
				{result && result.length > 0 && <InvoiceRouting invoices={result} onRouted={setRouted} />}
				<DialogActions>
					<Button onClick={clearOpen} color="inherit">{t("actions.close")}</Button>
				</DialogActions>
			</Dialog>
		</Fragment>
	);
}

function useRoutableInvoices(visible: boolean, invoices: InvoiceSummary[]) {
	const {getInvoice} = useInvoicesApi();
	const user = useUser();

	return useAsync(async (visible: boolean, invoices: InvoiceSummary[]) => {
		// don't load anything, if button is not visible
		if (!visible) {
			return [];
		}

		// avoid loading the invoices, if they are not in the same inbox anyway
		if (!inSameInbox(invoices)) {
			return [];
		}
		const loadedInvoices = await Promise.all(invoices.map(invoice => getInvoice(invoice.id)));
		// return empty array if some invoices are not routable
		if (!loadedInvoices.every(invoice => canRouteInvoice(user, invoice))) {
			return [];
		}
		return loadedInvoices;
	}, [visible, invoices]);
}
